import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './App.css';
import { useAuth0 } from "@auth0/auth0-react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import config from './config';
import FAQ from './faq';
import Blogs from './Blogs';
import BlogArticle from './BlogArticle';
import Settings from './Settings';
import MyProfile from './MyProfile';
import MyEnrollments from './MyEnrollments';
import PracticeTasks from './PracticeTasks';
import Courses from './Courses';
import CourseDetails from './CourseDetails';
import Home from './Home';
import ForTutorsLanding from './ForTutorsLanding';
import HowItWorks from './HowItWorks';
import WhatWeDo from './WhatWeDo';
import PracticeTests from './PracticeTestsPage';
import { ExamCenter } from './ExamCenter';
import { ExamQuiz } from './ExamQuiz';
import { ExamAttemptReview } from './ExamAttemptReview';
import { PracticeTestQuiz } from './PracticeTestQuiz';
import { PracticeTestResults } from './PracticeTestResults';
import { HomeProvider } from './HomeContext';
import ProductDetails from './ProductDetails';
import Compare from './Compare';
import Dashboard from './components/Dashboard';
import ManageCategories from './admin/ManageCategories';
import ManageCategoriesV2 from './admin/ManageCategoriesV2';
import ManageClassrooms from './admin/ManageClassrooms';
import ManageCourses from './admin/ManageCourses';
import ManageExams from './admin/ManageExams';
import ManageAssignments from './admin/ManageAssignments';
import ManageSchedule from './admin/ManageSchedule';
import ManageSessions from './admin/ManageSessions';
import ManageScheduleDetail from './admin/ManageScheduleDetail';
import ManageSessionDetail from './admin/ManageSessionDetail';
import ReportTeacherHours from './admin/ReportTeacherHours';
import ReportStudentSessions from './admin/ReportStudentSessions';
import AssignmentsHub from './AssignmentsHub';
import AssignmentHomework from './AssignmentHomework';
import MySchedule from './MySchedule';
import MySessions from './MySessions';
import MySessionPage from './MySessionPage';
import ManageQuestions from './admin/ManageQuestions';
import ManageDiagrams from './admin/ManageDiagrams';
import ManageCategoryDiagrams from './admin/ManageCategoryDiagrams';
import ManageDynamicComponents from './admin/ManageDynamicComponents';
import ManageSiteContent from './admin/ManageSiteContent';
import ManageEmailTemplates from './admin/ManageEmailTemplates';
import AdminLayout from './admin/AdminLayout';
import CustomerLayout from './CustomerLayout';
import Billing from './Billing';
import SubscriptionSuccess from './SubscriptionSuccess';
import BookManagement from './admin/BookManagement';
import ManageFreeResources from './admin/ManageFreeResources';
import ManageDocuments from './admin/ManageDocuments';
import ManageOrganizations from './admin/ManageOrganizations';
import ManageOrganizationMemberships from './admin/ManageOrganizationMemberships';
import ManageOrganizationMembershipRoles from './admin/ManageOrganizationMembershipRoles';
import ManageOrganizationMembersAndRoles from './admin/ManageOrganizationMembersAndRoles';
import ManageOrganizationRequests from './admin/ManageOrganizationRequests';
import ManagePlatformUserRoles from './admin/ManagePlatformUserRoles';
import SubmissionReview from './admin/SubmissionReview';
import ContactUs from './ContactUs';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import DataDeletion from './DataDeletion';
import { initializeAnalytics, trackPageView } from './analytics';
import OrganizationPreDashboard from './OrganizationPreDashboard';
import OrganizationOnboarding from './OrganizationOnboarding';
import OrganizationInviteAccept from './OrganizationInviteAccept';
import AdminDashboard from './admin/AdminDashboard';
import { clearOrganizationContext, getOrganizationContext, setOrganizationContext } from './orgContext';
import { filterNavItems, getMembershipRoles, getUserRoles, mergeRoles, settingsDropdownLinks } from './navigationConfig';
import { shouldRedirectAdminAccess } from './adminAccess';
import authConfig from './authConfig';
import TopNavigation from './components/TopNavigation';


const TRACKED_EXACT_PATHS = new Set([
  '/',
  '/for-tutors',
  '/courses',
  '/practice',
  '/blogs',
  '/how-it-works',
  '/what-we-do',
  '/compare',
  '/faq',
  '/contact',
]);

const TRACKED_PREFIX_PATHS = [
  '/course-details/',
  '/practice-tests/',
  '/exam-center/',
  '/exam/',
  '/exam-attempt/',
  '/practice-test/',
  '/practice-result/',
  '/product/',
  '/blogs/',
];

const shouldTrackRoute = (pathname: string): boolean => {
  if (TRACKED_EXACT_PATHS.has(pathname)) {
    return true;
  }

  return TRACKED_PREFIX_PATHS.some(prefix => pathname.startsWith(prefix));
};


function RouteAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!shouldTrackRoute(location.pathname)) {
      return;
    }

    initializeAnalytics();

    const path = `${location.pathname}${location.search}`;
    trackPageView(path);
  }, [location.pathname, location.search]);

  return null;
}

function RouterLocationSync({ onPathChange }: { onPathChange: (pathname: string) => void }) {
  const location = useLocation();

  useEffect(() => {
    onPathChange(location.pathname);
  }, [location.pathname, onPathChange]);

  return null;
}

function RoleRouteGuard({
  isAuthenticated,
  isAuthLoading,
  profileCompletionRequired,
  hasOrganizationMembership,
  hasActiveOrganizationContext,
  isStudentUser,
  activeOrganizationSlug,
  publicOrgSlug,
}: {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  profileCompletionRequired: boolean;
  hasOrganizationMembership: boolean;
  hasActiveOrganizationContext: boolean;
  isStudentUser: boolean;
  activeOrganizationSlug: string;
  publicOrgSlug: string;
}) {
  const location = useLocation();

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const pathname = location.pathname;
    if (profileCompletionRequired && pathname !== '/profile') {
      return;
    }

    const isAdmin = isAdminRoute(pathname);
    const hasOrgAccess = hasOrganizationMembership || hasActiveOrganizationContext;

    if (isAuthenticated) {
      if (!hasOrgAccess) {
        return;
      }

      if (isStudentUser && isAdmin) {
        const targetSlug = activeOrganizationSlug || publicOrgSlug;
        const fallbackPath = `/${targetSlug}/analytics`;
        if (pathname !== fallbackPath) {
          window.location.replace(fallbackPath);
        }
      }
      return;
    }

    if (isAdmin || pathname === '/analytics') {
      const guestTarget = `/${publicOrgSlug}/analytics`;
      if (pathname !== guestTarget) {
        window.location.replace(guestTarget);
      }
    }
  }, [
    activeOrganizationSlug,
    hasActiveOrganizationContext,
    hasOrganizationMembership,
    isAuthLoading,
    isAuthenticated,
    isStudentUser,
    location.pathname,
    profileCompletionRequired,
    publicOrgSlug,
  ]);

  return null;
}


const isAdminRoute = (pathname: string) => pathname === '/admin' || pathname.startsWith('/manage-') || pathname.startsWith('/admin/') || pathname === '/books';

const isCustomerLayoutRoute = (pathname: string): boolean => {
  const p = pathname.toLowerCase();
  // Strip a potential org-slug prefix (e.g. /atar_classes/practice → /practice)
  const noSlug = p.replace(/^\/[^\\/]+/, '');

  const match = (path: string) => (
    path === '/practice' || path.startsWith('/practice/') ||
    path === '/courses' || path.startsWith('/courses/') ||
    path === '/course-details' || path.startsWith('/course-details/') ||
    path === '/assignments' || path.startsWith('/assignments/') ||
    path === '/practice-tests' || path.startsWith('/practice-tests/') ||
    path.startsWith('/practice-test/') ||
    path.startsWith('/practice-result/') ||
    path.startsWith('/exam-center/') ||
    path.startsWith('/exam/') ||
    path.startsWith('/exam-attempt/') ||
    path === '/my-schedule' || path.startsWith('/my-schedule/') ||
    path === '/my-sessions' || path.startsWith('/my-sessions/') ||
    path.startsWith('/my-session/') ||
    path === '/my-enrollments' ||
    path === '/billing' || path.startsWith('/billing/') ||
    path === '/settings' || path.startsWith('/settings/')
  );

  // /:orgSlug/analytics — only matches when there IS an org prefix (not plain /analytics)
  const isOrgAnalytics = p !== '/analytics' && p.endsWith('/analytics');

  return match(p) || match(noSlug) || isOrgAnalytics;
};
const isAdminTopBarHiddenRoute = (pathname: string) => isAdminRoute(pathname);

const redirectAdminAccess = () => {
  const currentPath = window.location.pathname;
  const fallbackPath = '/analytics';

  if (currentPath === fallbackPath || currentPath === '/analytics?pre=1') {
    return;
  }

  if (!shouldRedirectAdminAccess(currentPath)) {
    return;
  }

  window.location.assign(fallbackPath);
};

function App() {
  const { loginWithRedirect, isAuthenticated, isLoading: isAuthLoading, user, getAccessTokenSilently, logout } = useAuth0();
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);
  const publicOrgSlug = (config.publicOrgSlug || 'em').trim().toLowerCase();
  const legacyPublicOrgSlugs = (config.publicOrgLegacySlugs || [])
    .map((slug) => String(slug).trim().toLowerCase())
    .filter((slug) => !!slug && slug !== publicOrgSlug);
  const [membershipRoles, setMembershipRoles] = useState<string[]>([]);
  const [membershipOrganizations, setMembershipOrganizations] = useState<any[]>([]);
  const [hasOrganizationMembership, setHasOrganizationMembership] = useState(false);
  const [profileCompletionRequired, setProfileCompletionRequired] = useState(false);
  const hasActiveOrganizationContext = Boolean(getOrganizationContext());
  const isPreOrganizationDashboard = currentPathname === '/analytics' && new URLSearchParams(window.location.search).get('pre') === '1';
  const hasUnlockedNavigation = !isPreOrganizationDashboard && (hasOrganizationMembership || hasActiveOrganizationContext);
  const showFullNavigation = isAuthenticated && !profileCompletionRequired && hasUnlockedNavigation;
  const showUserSettingsMenu = isAuthenticated && !profileCompletionRequired && hasUnlockedNavigation;

  const userRoles = useMemo(() => mergeRoles(getUserRoles(user), membershipRoles), [membershipRoles, user]);
  const isStudentUser = useMemo(() => userRoles.includes('student'), [userRoles]);
  const visibleSettingsLinks = useMemo(() => filterNavItems(settingsDropdownLinks, isAuthenticated, userRoles), [isAuthenticated, userRoles]);
  const displayUserName = useMemo(() => {
    if (!user) {
      return '';
    }

    return user.name || user.given_name || user.family_name || user.nickname || user.email || 'User';
  }, [user]);

  const reservedTopLevelPaths = new Set([
    'courses', 'course-details', 'practice', 'practice-tests', 'exam-center', 'exam', 'exam-attempt',
    'practice-test', 'practice-result', 'analytics', 'my-enrollments', 'faq', 'contact', 'privacy',
    'terms', 'data-deletion', 'blogs', 'product', 'compare', 'profile', 'settings', 'manage-categories',
    'manage-classrooms', 'manage-assignments', 'manage-courses', 'manage-exams', 'manage-questions',
    'manage-diagrams', 'manage-category-diagrams', 'manage-dynamic-components', 'manage-free-resources',
    'manage-email-templates',
    'manage-documents', 'manage-organizations', 'manage-organization-memberships',
    'manage-organization-members-and-roles', 'manage-organization-membership-roles', 'manage-organization-requests', 'manage-platform-user-roles',
    'admin', 'billing', 'books', 'subscription-success', 'organization-setup', 'organization-invite'
  ]);
  const pathSegments = currentPathname.split('/').filter(Boolean);
  const firstPathSegment = (pathSegments[0] || '').toLowerCase();
  const shouldRedirectLegacyPublicOrg = legacyPublicOrgSlugs.includes(firstPathSegment);
  const routeOrganizationSlug =
    pathSegments.length >= 2 && !reservedTopLevelPaths.has(pathSegments[0])
      ? pathSegments[0]
      : '';
  const contextOrganizationSlug = getOrganizationContext()?.organizationSlug ?? '';
  const activeOrganizationSlug = routeOrganizationSlug || contextOrganizationSlug;
  const activeOrganizationName = useMemo(() => {
    const context = getOrganizationContext();
    if (
      context?.organizationName &&
      (!activeOrganizationSlug || context.organizationSlug.toLowerCase() === activeOrganizationSlug.toLowerCase())
    ) {
      return context.organizationName;
    }

    if (!activeOrganizationSlug) {
      return '';
    }

    const matchedOrganization = membershipOrganizations.find(
      (item: any) => String(item?.organizationSlug || '').toLowerCase() === activeOrganizationSlug.toLowerCase()
    );

    return String(matchedOrganization?.organizationName || matchedOrganization?.name || matchedOrganization?.displayName || '');
  }, [activeOrganizationSlug, membershipOrganizations]);
  const orgAwarePath = (path: string) =>
    isAuthenticated && activeOrganizationSlug ? `/${activeOrganizationSlug}${path}` : path;

  const requireProfileCompletion = (element: React.ReactNode) =>
    isAuthenticated && profileCompletionRequired ? <Navigate to="/profile" replace /> : element;

  const withAdminLayout = (element: React.ReactElement) => (
    <AdminLayout>{element}</AdminLayout>
  );

  const withCustomerLayout = (element: React.ReactElement) => (
    <CustomerLayout>{element}</CustomerLayout>
  );

  const withCustomerLayoutCollapsed = (element: React.ReactElement) => (
    <CustomerLayout autoCollapse>{element}</CustomerLayout>
  );

  useEffect(() => {
    if (!shouldRedirectLegacyPublicOrg) {
      return;
    }

    const updatedSegments = [...pathSegments];
    updatedSegments[0] = publicOrgSlug;
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    const nextPath = `/${updatedSegments.join('/')}${search}${hash}`;
    window.location.replace(nextPath);
  }, [pathSegments, publicOrgSlug, shouldRedirectLegacyPublicOrg]);

  const handleLogin = () => {
    const returnTo = '/analytics?pre=1';
    sessionStorage.setItem('postLoginReturnTo', returnTo);
    loginWithRedirect({
      appState: {
        returnTo,
      },
    });
  };

  const handleLogout = () => {
    clearOrganizationContext();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  useEffect(() => {
    const axiosInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const pathname = window.location.pathname;

        if (status === 403 && isAdminRoute(pathname)) {
          redirectAdminAccess();
        }

        return Promise.reject(error);
      }
    );

    const originalFetch = window.fetch.bind(window);
    const fetchWrapper = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await originalFetch(input, init);
        if (response.status === 403 && isAdminRoute(window.location.pathname)) {
          redirectAdminAccess();
        }
        return response;
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 403 && isAdminRoute(window.location.pathname)) {
          redirectAdminAccess();
        }
        throw error;
      }
    };

    window.fetch = fetchWrapper as typeof window.fetch;

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      window.fetch = originalFetch;
    };
  }, []);


  useEffect(() => {
    const userSub = user?.sub;
    if (!isAuthenticated || !userSub) {
      setProfileCompletionRequired(false);
      return;
    }

    let cancelled = false;

    const checkProfileCompletion = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: authConfig.audience,
          },
        } as any);

        const response = await fetch(`${config.apiBaseUrl}/api/profile/${encodeURIComponent(userSub)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (!cancelled) {
            setProfileCompletionRequired(true);
          }
          return;
        }

        const profile = await response.json();
        const requiredFields = [
          profile?.firstName,
          profile?.lastName,
          profile?.phone,
          profile?.address,
          profile?.city,
          profile?.state,
          profile?.zipCode,
          profile?.country,
        ];

        if (!cancelled) {
          setProfileCompletionRequired(requiredFields.some((value) => !String(value ?? '').trim()));
        }
      } catch (error) {
        console.warn('Unable to verify profile completion', error);
        if (!cancelled) {
          setProfileCompletionRequired(true);
        }
      }
    };

    void checkProfileCompletion();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, user?.sub]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub || !profileCompletionRequired) {
      return;
    }

    const currentLocation = window.location.pathname;
    if (currentLocation !== '/profile') {
      window.location.assign('/profile');
    }
  }, [isAuthenticated, profileCompletionRequired, user?.sub]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMembershipRoles([]);
      setMembershipOrganizations([]);
      setHasOrganizationMembership(false);
      clearOrganizationContext();
      return;
    }

    let cancelled = false;

    const loadMembershipRoles = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (!cancelled) {
            setMembershipRoles([]);
            setMembershipOrganizations([]);
            setHasOrganizationMembership(false);
          }
          return;
        }

        const organizations = await response.json();
        const hasMembership = Array.isArray(organizations) && organizations.length > 0;
        if (!cancelled) {
          setMembershipOrganizations(organizations);
          setMembershipRoles(getMembershipRoles(organizations));
          setHasOrganizationMembership(hasMembership);

          if (!getOrganizationContext() && hasMembership) {
            const fallbackOrganization = organizations.find((item: any) => Boolean(item?.isDefault)) ?? organizations[0];
            if (fallbackOrganization?.organizationId && fallbackOrganization?.organizationSlug) {
              setOrganizationContext({
                organizationId: Number(fallbackOrganization.organizationId),
                organizationSlug: String(fallbackOrganization.organizationSlug),
                organizationName: String(fallbackOrganization.organizationName || fallbackOrganization.name || fallbackOrganization.displayName || ''),
              });
            }
          }
        }
      } catch (error) {
        console.warn('Unable to load organization membership roles', error);
        if (!cancelled) {
          setMembershipRoles([]);
          setMembershipOrganizations([]);
          setHasOrganizationMembership(false);
        }
      }
    };

    void loadMembershipRoles();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!activeOrganizationSlug || !isAuthenticated) {
      return;
    }

    const existingContext = getOrganizationContext();
    if (existingContext?.organizationSlug?.toLowerCase() === activeOrganizationSlug.toLowerCase()) {
      return;
    }

    let cancelled = false;

    const syncOrganizationContextFromSlug = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${config.apiBaseUrl}/api/organizations/my`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const organizations = await response.json();
        if (!Array.isArray(organizations)) {
          return;
        }

        const matchedOrganization = organizations.find(
          (item: any) => String(item.organizationSlug || '').toLowerCase() === activeOrganizationSlug.toLowerCase()
        );

        if (!cancelled && matchedOrganization?.organizationId && matchedOrganization?.organizationSlug) {
          setOrganizationContext({
            organizationId: Number(matchedOrganization.organizationId),
            organizationSlug: String(matchedOrganization.organizationSlug),
            organizationName: String(matchedOrganization.organizationName || matchedOrganization.name || matchedOrganization.displayName || ''),
          });
        }
      } catch (error) {
        console.warn('Unable to sync organization context from URL slug', error);
      }
    };

    syncOrganizationContextFromSlug();

    return () => {
      cancelled = true;
    };
  }, [activeOrganizationSlug, isAuthenticated, getAccessTokenSilently]);

  return (
    <HomeProvider>
      <Router>
        <RouterLocationSync onPathChange={setCurrentPathname} />
        <RoleRouteGuard
          isAuthenticated={isAuthenticated}
          isAuthLoading={isAuthLoading}
          profileCompletionRequired={profileCompletionRequired}
          hasOrganizationMembership={hasOrganizationMembership}
          hasActiveOrganizationContext={hasActiveOrganizationContext}
          isStudentUser={isStudentUser}
          activeOrganizationSlug={activeOrganizationSlug}
          publicOrgSlug={publicOrgSlug}
        />
        <RouteAnalyticsTracker />
        <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header with menu */}
          {(() => {
            const orgCtx = getOrganizationContext();
            // Also derive org presence from the live URL — first segment of /:orgSlug/... routes
            const liveSegments = window.location.pathname.split('/').filter(Boolean);
            const liveSlug = liveSegments.length > 1 ? liveSegments[0] : '';
            const hasOrg = !!(orgCtx?.organizationId || orgCtx?.organizationSlug || activeOrganizationSlug || liveSlug);
            const livePath = window.location.pathname;
            const hideForCustomer = isAuthenticated && hasOrg && isCustomerLayoutRoute(livePath);
            return !isAdminTopBarHiddenRoute(livePath) && !hideForCustomer;
          })() && (
            <TopNavigation
              isAuthenticated={isAuthenticated}
              displayUserName={displayUserName}
              organizationDisplayName={activeOrganizationName}
              showFullNavigation={showFullNavigation}
              showUserSettingsMenu={showUserSettingsMenu}
              visibleSettingsLinks={visibleSettingsLinks}
              homePath={orgAwarePath('/')}
              orgAwarePath={orgAwarePath}
              guestOrgBasePath={`/${publicOrgSlug}`}
              restrictToLearnerMenu={isStudentUser || !isAuthenticated}
              onLogin={handleLogin}
              onLogout={handleLogout}
            />
          )}

          {/* Main content with routes */}
          <main style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={
                <Home
                  user={user}
                  loginWithRedirect={loginWithRedirect}
                  getAccessTokenSilently={getAccessTokenSilently}
                />
              } />
              <Route path="/for-tutors" element={
                <ForTutorsLanding
                  isAuthenticated={isAuthenticated}
                  onStartPilotLogin={handleLogin}
                />
              } />
            
              <Route path="/courses" element={withCustomerLayout(<Courses />)} />
              <Route path="/:organizationSlug/courses" element={withCustomerLayout(<Courses />)} />
              <Route path="/courses/:courseRef" element={withCustomerLayout(<Courses />)} />
              <Route path="/:organizationSlug/courses/:courseRef" element={withCustomerLayout(<Courses />)} />
              <Route path="/course-details/:courseId" element={withCustomerLayout(<CourseDetails />)} />
              <Route path="/:organizationSlug/course-details/:courseId" element={withCustomerLayout(<CourseDetails />)} />
              <Route path="/practice" element={withCustomerLayout(<PracticeTasks />)} />
              <Route path="/:organizationSlug/practice" element={withCustomerLayout(<PracticeTasks />)} />
              <Route path="/assignments" element={withCustomerLayout(<AssignmentsHub />)} />
              <Route path="/:organizationSlug/assignments" element={withCustomerLayout(<AssignmentsHub />)} />
              <Route path="/assignments/homework" element={withCustomerLayout(<AssignmentHomework />)} />
              <Route path="/:organizationSlug/assignments/homework" element={withCustomerLayout(<AssignmentHomework />)} />
              <Route path="/assignments/:assignmentId/homework" element={withCustomerLayout(<AssignmentHomework />)} />
              <Route path="/:organizationSlug/assignments/:assignmentId/homework" element={withCustomerLayout(<AssignmentHomework />)} />
              <Route path="/practice-tests" element={withCustomerLayout(<PracticeTests />)} />
              <Route path="/:organizationSlug/practice-tests" element={withCustomerLayout(<PracticeTests />)} />
              <Route path="/practice-tests/:courseId" element={withCustomerLayout(<PracticeTests />)} />
              <Route path="/:organizationSlug/practice-tests/:courseId" element={withCustomerLayout(<PracticeTests />)} />
              <Route path="/exam-center/:id" element={withCustomerLayout(<ExamCenter />)} />
              <Route path="/:organizationSlug/exam-center/:id" element={withCustomerLayout(<ExamCenter />)} />
              <Route path="/exam/:id" element={withCustomerLayoutCollapsed(<ExamQuiz />)} />
              <Route path="/:organizationSlug/exam/:id" element={withCustomerLayoutCollapsed(<ExamQuiz />)} />
              <Route path="/exam-attempt/:attemptId" element={withCustomerLayout(<ExamAttemptReview />)} />
              <Route path="/:organizationSlug/exam-attempt/:attemptId" element={withCustomerLayout(<ExamAttemptReview />)} />
              <Route path="/practice-test/:id" element={withCustomerLayoutCollapsed(<PracticeTestQuiz />)} />
              <Route path="/:organizationSlug/practice-test/:id" element={withCustomerLayoutCollapsed(<PracticeTestQuiz />)} />
              <Route path="/practice-result/:attemptId" element={withCustomerLayout(<PracticeTestResults />)} />
              <Route path="/:organizationSlug/practice-result/:attemptId" element={withCustomerLayout(<PracticeTestResults />)} />
              <Route path="/analytics" element={requireProfileCompletion(<OrganizationPreDashboard />)} />
              <Route path="/:organizationSlug/analytics" element={withCustomerLayout(requireProfileCompletion(<Dashboard />) as React.ReactElement)} />
              <Route path="/organization-setup" element={<OrganizationOnboarding />} />
              <Route path="/organization-invite" element={<OrganizationInviteAccept />} />
              <Route path="/my-enrollments" element={withCustomerLayout(<MyEnrollments />)} />
              <Route path="/my-schedule" element={withCustomerLayout(<MySchedule />)} />
              <Route path="/:organizationSlug/my-schedule" element={withCustomerLayout(<MySchedule />)} />
              <Route path="/my-sessions" element={withCustomerLayout(<MySessions />)} />
              <Route path="/:organizationSlug/my-sessions" element={withCustomerLayout(<MySessions />)} />
              <Route path="/my-session/:id" element={withCustomerLayout(<MySessionPage />)} />
              <Route path="/:organizationSlug/my-session/:id" element={withCustomerLayout(<MySessionPage />)} />
              <Route path="/manage-schedule" element={withAdminLayout(<ManageSchedule />)} />
              <Route path="/manage-schedule/all" element={withAdminLayout(<ManageSessions scope="all" />)} />
              <Route path="/manage-schedule/mysessions" element={withAdminLayout(<ManageSessions scope="my" />)} />
              <Route path="/manage-schedule/session/:sessionId" element={withAdminLayout(<ManageSessionDetail />)} />
              <Route path="/manage-schedule/new" element={withAdminLayout(<ManageScheduleDetail />)} />
              <Route path="/manage-schedule/:id" element={withAdminLayout(<ManageScheduleDetail />)} />
              <Route path="/reports/teacher-hours" element={withAdminLayout(<ReportTeacherHours />)} />
              <Route path="/reports/student-sessions" element={withAdminLayout(<ReportStudentSessions />)} />
              <Route path="/:organizationSlug/manage-schedule" element={withAdminLayout(<ManageSchedule />)} />
              <Route path="/:organizationSlug/manage-schedule/all" element={withAdminLayout(<ManageSessions scope="all" />)} />
              <Route path="/:organizationSlug/manage-schedule/mysessions" element={withAdminLayout(<ManageSessions scope="my" />)} />
              <Route path="/:organizationSlug/manage-schedule/session/:sessionId" element={withAdminLayout(<ManageSessionDetail />)} />
              <Route path="/:organizationSlug/manage-schedule/new" element={withAdminLayout(<ManageScheduleDetail />)} />
              <Route path="/:organizationSlug/manage-schedule/:id" element={withAdminLayout(<ManageScheduleDetail />)} />
                <Route path="/faq" element={<FAQ />} />
              <Route path="/:organizationSlug/faq" element={<FAQ />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/:organizationSlug/contact" element={<ContactUs />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/:organizationSlug/blogs" element={<Blogs />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/what-we-do" element={<WhatWeDo />} />
              <Route path="/blogs/:slug" element={<BlogArticle />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/profile" element={<MyProfile />} />
              <Route path="/settings" element={withCustomerLayout(<Settings />)} />
              <Route path="/admin" element={withAdminLayout(<AdminDashboard />)} />
              <Route path="/manage-categories" element={withAdminLayout(<ManageCategories />)} />
              <Route path="/manage-categories-v2" element={withAdminLayout(<ManageCategoriesV2 />)} />
              <Route path="/manage-classrooms" element={withAdminLayout(<ManageClassrooms />)} />
              <Route path="/manage-assignments" element={withAdminLayout(<ManageAssignments />)} />
              <Route path="/manage-assignment-builder" element={withAdminLayout(<ManageAssignments />)} />
              <Route path="/admin/submissions/:attemptId" element={withAdminLayout(<SubmissionReview />)} />
              <Route path="/manage-courses" element={withAdminLayout(<ManageCourses />)} />
              <Route path="/manage-exams" element={withAdminLayout(<ManageExams />)} />
              <Route path="/manage-exams/:examId/questions" element={withAdminLayout(<ManageExams />)} />
              <Route path="/manage-questions" element={withAdminLayout(<ManageQuestions />)} />
              <Route path="/manage-diagrams" element={withAdminLayout(<ManageDiagrams />)} />
              <Route path="/manage-category-diagrams" element={withAdminLayout(<ManageCategoryDiagrams />)} />
              <Route path="/manage-dynamic-components" element={withAdminLayout(<ManageDynamicComponents />)} />
              <Route path="/manage-site-content" element={withAdminLayout(<ManageSiteContent />)} />
              <Route path="/manage-email-templates" element={withAdminLayout(<ManageEmailTemplates />)} />
              <Route path="/manage-free-resources" element={withAdminLayout(<ManageFreeResources />)} />
              <Route path="/manage-documents" element={withAdminLayout(<ManageDocuments />)} />
              <Route path="/manage-organizations" element={withAdminLayout(<ManageOrganizations />)} />
              <Route path="/manage-organization-memberships" element={withAdminLayout(<ManageOrganizationMemberships />)} />
              <Route path="/manage-organization-membership-roles" element={withAdminLayout(<ManageOrganizationMembershipRoles />)} />
              <Route path="/manage-organization-members-and-roles" element={withAdminLayout(<ManageOrganizationMembersAndRoles />)} />
              <Route path="/manage-organization-requests" element={withAdminLayout(<ManageOrganizationRequests />)} />
              <Route path="/manage-platform-user-roles" element={withAdminLayout(<ManagePlatformUserRoles />)} />
              <Route path="/billing" element={withCustomerLayout(<Billing />)} />
              <Route path="/:organizationSlug/billing" element={withCustomerLayout(<Billing />)} />
              <Route path="/books" element={withAdminLayout(<BookManagement />)} />
              <Route path="/subscription-success" element={<SubscriptionSuccess />} />
              
              {/* Catch-all route - redirect any unmatched routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer style={{
            background: '#222',
            color: '#fff',
            textAlign: 'center',
            padding: '18px 0',
            marginTop: 'auto'
          }}>
            <div style={{ marginBottom: 8 }}>
              <Link to={orgAwarePath('/faq')} style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >FAQ</Link>
              <Link to={orgAwarePath('/contact')} style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Contact Us</Link>
              <Link to="/privacy" style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Privacy Policy</Link>
              <Link to="/terms" style={{ color: '#a5b4fc', fontSize: 13, marginRight: 20, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Terms of Service</Link>
              <Link to="/data-deletion" style={{ color: '#a5b4fc', fontSize: 13, textDecoration: 'none' }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >Data Deletion</Link>
            </div>
            &copy; {new Date().getFullYear()} ExamMasters &mdash; All rights reserved.
          </footer>
          
        </div>
      </Router>
    </HomeProvider>
  );
}

export default App;